/**
 * Main Entry Point for Astrophotography Planning Calculator
 * 
 * This module initializes the application and sets up all event handlers
 * for the JavaScript implementation of the astronomical imaging calculator.
 */

import AstroImagingApp from './app.js';
import { formatRA, formatDec, formatLatitude, formatLongitude } from './coordinateFormat.js';

// Global application instance
let app = null;
let suggestionTimeout = null;

/**
 * Initialize the application
 */
async function initializeApp() {
    try {
        console.log('Initializing Astrophotography Planning Calculator...');
        
        // Create application instance
        app = new AstroImagingApp();

        // Until the real handler is attached, keep Calculate from doing a native
        // form submit (which would reload the page mid catalogue import)
        const form = document.getElementById('calculationForm');
        const blockSubmit = (e) => e.preventDefault();
        form.addEventListener('submit', blockSubmit);

        // Initialize the application (shows progress if the catalogue must be reloaded)
        let reloadingCatalogue = false;
        const success = await app.initialize({
            onDataReload: () => {
                reloadingCatalogue = true;
                showLoading('Updating the object catalogue (one-time, about 30 seconds)...');
            }
        });
        form.removeEventListener('submit', blockSubmit);
        if (reloadingCatalogue) hideLoading();
        
        if (success) {
            console.log('Application initialized successfully');
            updateObjectSearchHint(app.searchEngine.objectCount);
            setupEventListeners();
            setupUI();
        } else {
            console.error('Failed to initialize application');
            showError('Failed to initialize application. Please refresh the page.');
        }
    } catch (error) {
        console.error('Initialization error:', error);
        showError('Initialization error: ' + error.message);
    }
}

/**
 * Show the live catalogue size under the object search box
 * @param {number} objectCount - Objects in the loaded catalogue
 */
function updateObjectSearchHint(objectCount) {
    const hint = document.getElementById('objectSearchHint');
    if (hint && objectCount > 0) {
        hint.textContent = `Search ${objectCount.toLocaleString('en-US')} catalog objects or use manual coordinates below`;
    }
}

/**
 * Setup event listeners
 */
function setupEventListeners() {
    // Form submission
    document.getElementById('calculationForm').addEventListener('submit', handleFormSubmit);
    
    // Search button
    document.getElementById('searchBtn').addEventListener('click', handleSearchObject);
    
    // Autosuggestions disabled: do not attach input handler
    
    // Altitude plot button
    document.getElementById('createAltitudePlot').addEventListener('click', handleCreateAltitudePlot);
    
    // Location selector change
    document.getElementById('locationSelector').addEventListener('change', handleLocationSelectorChange);
    
    // Autosuggestions disabled: no dropdown handlers
    
    // Window resize handler
    window.addEventListener('resize', handleWindowResize);

    // Use my location (nearest town) for Altitude Plot
    const useMyLocationBtn = document.getElementById('useMyLocationBtn');
    if (useMyLocationBtn) {
        useMyLocationBtn.addEventListener('click', handleUseMyLocation);
    }

    // No toggle button needed - both views are now separate sections

    // Prevent page scrolling when interacting with 3D plots
    setup3DPlotScrollPrevention();

    // Sky Path time slider controls
    setupSkyPathTimeControls();
    setupSkySphereTimeControls();
    
    // Manual coordinates checkbox
    setupManualCoordinatesToggle();

    // Planet target checkbox
    setupPlanetToggle();

    // Moon filter checkboxes
    setupMoonFilterToggles();

    // Online/offline indicator removed
}

/**
 * Handle "Use My Location": get browser geolocation, reverse geocode nearest town,
 * fill lat/lon, update timezone hint, and show town label.
 */
async function handleUseMyLocation() {
    const btn = document.getElementById('useMyLocationBtn');
    const townEl = document.getElementById('nearestTownDisplay');
    try {
        if (!navigator.geolocation) {
            showError('Geolocation not supported by this browser.');
            return;
        }
        if (btn) { btn.disabled = true; btn.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Locating...'; }
        if (townEl) townEl.textContent = '';

        const pos = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 300000
            });
        });
        const lat = pos.coords.latitude;
        const lon = pos.coords.longitude;

        // Fill inputs for Altitude Plot (force dot decimal separator)
        document.getElementById('altitudeLatitude').value = lat.toFixed(4).replace(',', '.');
        document.getElementById('altitudeLongitude').value = lon.toFixed(4).replace(',', '.');

        // Also update Observation Locations section: replace defaults with this single location
        const latList = document.getElementById('latitudes');
        const lonList = document.getElementById('longitudes');
        if (latList && lonList) {
            latList.value = lat.toFixed(4).replace(',', '.');
            lonList.value = lon.toFixed(4).replace(',', '.');
        }

        // Update Location selector options based on new Observation Locations
        try { updateLocationSelector(); } catch (_) {}

        // Try reverse geocode (OpenStreetMap Nominatim)
        try {
            const town = await reverseGeocodeNearestTown(lat, lon);
            if (townEl) townEl.textContent = town ? `Nearest: ${town}` : '';
        } catch (_) {
            if (townEl) townEl.textContent = '';
        }
    } catch (err) {
        if (err && err.code === 1) {
            showError('Location permission denied.');
        } else if (err && err.code === 2) {
            showError('Position unavailable.');
        } else if (err && err.code === 3) {
            showError('Geolocation timeout.');
        } else {
            showError('Failed to obtain location.');
        }
    } finally {
        if (btn) { btn.disabled = false; btn.innerHTML = '<i class="fas fa-location-arrow"></i> Use My Location'; }
    }
}

/**
 * Reverse geocode nearest town using OpenStreetMap Nominatim.
 * Returns a concise place string like "Town, State, Country".
 */
async function reverseGeocodeNearestTown(lat, lon) {
    const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lon)}&zoom=10&addressdetails=1`;
    const resp = await fetch(url, { headers: { 'Accept': 'application/json' } });
    if (!resp.ok) throw new Error('Reverse geocode failed');
    const data = await resp.json();
    const addr = data && data.address ? data.address : {};
    // Prefer town/city/village/hamlet in order
    const locality = addr.town || addr.city || addr.village || addr.hamlet || addr.suburb || null;
    const state = addr.state || addr.region || null;
    const country = addr.country || null;
    const parts = [locality, state, country].filter(Boolean);
    return parts.join(', ');
}

/**
 * Setup initial UI state
 */
function setupUI() {
    // Initialize date picker with today's date
    document.getElementById('altitudeDate').value = new Date().toISOString().split('T')[0];

    // Force dot decimal format in all number inputs
    forceDotDecimals();

    // Set default values
    document.getElementById('altitudeThreshold').value = '20.0';
    document.getElementById('sunAltThreshold').value = '-15.0';
    document.getElementById('moonAltThreshold').value = '0.0';
    document.getElementById('moonPhaseThreshold').value = '25';
    document.getElementById('calculationYear').value = new Date().getFullYear().toString();
    document.getElementById('altitudeLatitude').value = '42.0';
    document.getElementById('altitudeLongitude').value = '-6.0';

    // Provide a clean, multiline example for custom horizon input without showing literal \n
    const horizonTA = document.getElementById('horizonData');
    if (horizonTA) {
        horizonTA.rows = 8;
        horizonTA.placeholder = `One per line (preferred):
21.94 36.52
49.33 38.61
91.92 27.79

Also accepted:
CSV: 21.94,36.52; 49.33,38.61; 91.92,27.79
JSON: [[21.94,36.52],[49.33,38.61],[91.92,27.79]]`;
    }

    // Add event listeners to prevent comma decimals during input
    document.querySelectorAll('input[type="number"]').forEach(input => {
        input.addEventListener('input', function(e) {
            if (e.target.value.includes(',')) {
                e.target.value = e.target.value.replace(',', '.');
            }
        });
    });

    // Handle URL parameters (for opening from Object Finder)
    handleURLParameters();

    // Load coordinates for the default object (only if not pre-filled by URL params)
    const params = new URLSearchParams(window.location.search);
    if (!params.get('ra') || !params.get('dec')) {
        searchObject();
    }

    updateMoonFilterAvailability();
}

/**
 * Handle URL parameters to pre-fill form fields
 * Used when opening from Object Finder with object coordinates
 */
function handleURLParameters() {
    const params = new URLSearchParams(window.location.search);
    const ra = params.get('ra');
    const dec = params.get('dec');
    const name = params.get('name');
    const lat = params.get('lat');
    const lon = params.get('lon');
    const date = params.get('date');

    // Pre-fill location if provided
    if (lat && lon) {
        document.getElementById('latitudes').value = lat;
        document.getElementById('longitudes').value = lon;
        document.getElementById('altitudeLatitude').value = lat;
        document.getElementById('altitudeLongitude').value = lon;
        // Trigger location selector update
        updateLocationSelector();
    }

    // Pre-fill date if provided
    if (date) {
        document.getElementById('altitudeDate').value = date;
    }

    // Pre-fill manual coordinates if provided
    if (ra && dec) {
        const checkbox = document.getElementById('useManualCoords');
        const manualSection = document.getElementById('manualCoordsSection');
        const objectNameInput = document.getElementById('objectName');

        if (checkbox && manualSection) {
            checkbox.checked = true;
            manualSection.style.display = 'block';
            if (objectNameInput) objectNameInput.disabled = true;

            document.getElementById('manualRA').value = ra;
            document.getElementById('manualDec').value = dec;
            if (name) {
                document.getElementById('manualObjectName').value = decodeURIComponent(name);
            }

            // Display the coordinates
            const raNum = parseFloat(ra);
            const decNum = parseFloat(dec);
            if (!isNaN(raNum) && !isNaN(decNum)) {
                displayObjectCoordinates(name ? decodeURIComponent(name) : 'Custom Object', raNum, decNum);
            }
        }
    }
}

/**
 * Handle form submission
 */
async function handleFormSubmit(e) {
    e.preventDefault();
    await calculateImagingHours();
}

/**
 * Handle search object button click
 */
async function handleSearchObject() {
    await searchObject();
}

/**
 * Handle object name input with autocomplete
 */
function handleObjectNameInput(_e) { /* autosuggestions disabled */ }

/**
 * Handle document click (for hiding suggestions)
 */
function handleDocumentClick(_e) { /* autosuggestions disabled */ }

/**
 * Handle keyboard navigation in suggestions
 */
function handleObjectNameKeydown(_e) { /* autosuggestions disabled */ }

/**
 * Handle create altitude plot button click
 */
async function handleCreateAltitudePlot() {
    await createAltitudePlot();
}

/**
 * Handle location selector change
 */
function handleLocationSelectorChange() {
    const selectedValue = this.value;
    if (selectedValue !== 'custom') {
        const [lat, lon, timezone] = selectedValue.split(',');
        document.getElementById('altitudeLatitude').value = parseFloat(lat).toFixed(1);
        document.getElementById('altitudeLongitude').value = parseFloat(lon).toFixed(1);
        document.getElementById('locationSelector').setAttribute('data-timezone', timezone);
    }
}

/**
 * Handle window resize
 */
function handleWindowResize() {
    if (app && app.plotlyManager) {
        app.plotlyManager.resizeAllPlots();
        // Match Sky Path box to Altitude plot size and keep sphere round inside that box
        try { syncSkyPathBoxToAltitude(); } catch (_) {}
    }
}

/**
 * Render 2D Polar Sky Path
 */
async function renderSkyPath2D() {
    const locationSelector = document.getElementById('locationSelector');
    let selectedTimezone = locationSelector.getAttribute('data-timezone');
    if (!selectedTimezone) {
        const lat = parseFloat(document.getElementById('altitudeLatitude').value);
        const lon = parseFloat(document.getElementById('altitudeLongitude').value);
        selectedTimezone = app.calculator.getTimezoneForLocation(lat, lon);
    }
    const params = {
        object_name: document.getElementById('objectName').value,
        latitude: document.getElementById('altitudeLatitude').value,
        longitude: document.getElementById('altitudeLongitude').value,
        elevation: document.getElementById('altitudeElevation').value,
        obs_date: document.getElementById('altitudeDate').value,
        timezone: selectedTimezone,
        horizon_data: document.getElementById('horizonData') ? document.getElementById('horizonData').value : ''
    };
    
    // Planet target mode
    const usePlanet = document.getElementById('usePlanetTarget').checked;
    if (usePlanet) {
        const planetName = document.getElementById('planetName').value;
        params.planet = { name: planetName };
        params.object_name = planetName;
    }

    // Comet target mode (planet/Moon mode takes precedence over a stale comet)
    const targetType = document.body.getAttribute('data-target-type');
    if (targetType === 'comet' && !usePlanet) {
        const cometDesignation = document.body.getAttribute('data-comet-designation');
        params.comet = { designation: cometDesignation };
        params.object_name = cometDesignation;
    }

    // Add manual coordinates if being used
    const useManualCoords = document.getElementById('useManualCoords').checked && !document.getElementById('usePlanetTarget').checked && targetType !== 'comet';
    if (useManualCoords) {
        const raInput = document.getElementById('manualRA').value;
        const decInput = document.getElementById('manualDec').value;
        const customName = document.getElementById('manualObjectName').value || 'Custom Object';
        
        if (raInput.trim() && decInput.trim()) {
            const ra = parseRA(raInput);
            const dec = parseDecl(decInput);
            
            if (ra !== null && dec !== null) {
                params.manual_coords = {
                    ra_hours: ra,
                    dec_degrees: dec,
                    name: customName
                };
                params.object_name = customName; // Use custom name for display
            }
        }
    }

    const skyResult = await app.createSkyPathPlot(params);
    if (skyResult.success) {
        // Store plot data for animation
        skyPathPlotData = skyResult.plot;
        
        await app.plotlyManager.displayPlot('skyPathPlot', skyResult.plot);
        try { syncSkyPathBoxToAltitude(false); } catch (_) {}
        
        // Show time controls and initialize
        const timeControls = document.getElementById('skyPathTimeControls');
        if (timeControls) {
            timeControls.style.display = 'block';

            // Initialize time display
            const currentTimeSpan = document.getElementById('skyPathCurrentTime');
            const moonSepSpan = document.getElementById('skyPathMoonSep');
            // Separation from itself is meaningless when the Moon is the target
            if (moonSepSpan && moonSepSpan.parentElement) {
                moonSepSpan.parentElement.style.display = skyPathPlotData.isMoonTarget ? 'none' : '';
            }
            if (currentTimeSpan && skyPathPlotData.timePoints.length > 0) {
                currentTimeSpan.textContent = skyPathPlotData.timePoints[0].toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                });

                // Update Moon separation display
                if (moonSepSpan && skyPathPlotData.moonSeparations && skyPathPlotData.moonSeparations[0] !== null) {
                    moonSepSpan.textContent = skyPathPlotData.moonSeparations[0].toFixed(1) + '°';
                } else if (moonSepSpan) {
                    moonSepSpan.textContent = '--°';
                }
            }
            
            // Reset slider
            const slider = document.getElementById('skyPathTimeSlider');
            if (slider) {
                slider.value = 0;
            }
        }
    } else {
        throw new Error(skyResult.error || 'Failed to render sky path');
    }
}

/**
 * Render 3D Sphere Sky Path
 */
async function renderSkySphere3D() {
    const locationSelector = document.getElementById('locationSelector');
    let selectedTimezone = locationSelector.getAttribute('data-timezone');
    if (!selectedTimezone) {
        const lat = parseFloat(document.getElementById('altitudeLatitude').value);
        const lon = parseFloat(document.getElementById('altitudeLongitude').value);
        selectedTimezone = app.calculator.getTimezoneForLocation(lat, lon);
    }
    const params = {
        object_name: document.getElementById('objectName').value,
        latitude: document.getElementById('altitudeLatitude').value,
        longitude: document.getElementById('altitudeLongitude').value,
        elevation: document.getElementById('altitudeElevation').value,
        obs_date: document.getElementById('altitudeDate').value,
        timezone: selectedTimezone,
        horizon_data: document.getElementById('horizonData') ? document.getElementById('horizonData').value : ''
    };
    
    // Planet target mode
    const usePlanet = document.getElementById('usePlanetTarget').checked;
    if (usePlanet) {
        const planetName = document.getElementById('planetName').value;
        params.planet = { name: planetName };
        params.object_name = planetName;
    }

    // Comet target mode (planet/Moon mode takes precedence over a stale comet)
    const targetType = document.body.getAttribute('data-target-type');
    if (targetType === 'comet' && !usePlanet) {
        const cometDesignation = document.body.getAttribute('data-comet-designation');
        params.comet = { designation: cometDesignation };
        params.object_name = cometDesignation;
    }

    // Add manual coordinates if being used
    const useManualCoords = document.getElementById('useManualCoords').checked && !document.getElementById('usePlanetTarget').checked && targetType !== 'comet';
    if (useManualCoords) {
        const raInput = document.getElementById('manualRA').value;
        const decInput = document.getElementById('manualDec').value;
        const customName = document.getElementById('manualObjectName').value || 'Custom Object';
        
        if (raInput.trim() && decInput.trim()) {
            const ra = parseRA(raInput);
            const dec = parseDecl(decInput);
            
            if (ra !== null && dec !== null) {
                params.manual_coords = {
                    ra_hours: ra,
                    dec_degrees: dec,
                    name: customName
                };
                params.object_name = customName; // Use custom name for display
            }
        }
    }

    const skyResult = await app.createSkySpherePlot(params);
    if (skyResult.success) {
        // Store plot data for 3D animation
        skySpherePlotData = skyResult.plot;
        
        await app.plotlyManager.displayPlot('skySpherePlot', skyResult.plot);
        try { syncSkySphereBoxToAltitude(true); } catch (_) {}
        
        // Show time controls and initialize for 3D
        const timeControls = document.getElementById('skySphereTimeControls');
        if (timeControls) {
            timeControls.style.display = 'block';

            // Initialize time display
            const currentTimeSpan = document.getElementById('skySphereCurrentTime');
            const moonSepSpan = document.getElementById('skySphereMoonSep');
            // Separation from itself is meaningless when the Moon is the target
            if (moonSepSpan && moonSepSpan.parentElement) {
                moonSepSpan.parentElement.style.display = skySpherePlotData.isMoonTarget ? 'none' : '';
            }
            if (currentTimeSpan && skySpherePlotData.timePoints.length > 0) {
                currentTimeSpan.textContent = skySpherePlotData.timePoints[0].toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                });

                // Update Moon separation display
                if (moonSepSpan && skySpherePlotData.moonSeparations && skySpherePlotData.moonSeparations[0] !== null) {
                    moonSepSpan.textContent = skySpherePlotData.moonSeparations[0].toFixed(1) + '°';
                } else if (moonSepSpan) {
                    moonSepSpan.textContent = '--°';
                }
            }
            
            // Reset slider
            const slider = document.getElementById('skySphereTimeSlider');
            if (slider) {
                slider.value = 0;
            }
        }
    } else {
        throw new Error(skyResult.error || 'Failed to render sky sphere');
    }
}

/**
 * Make skyPathPlot use exactly the same width/height as altitudePlot's box.
 * For 3D, keep the dome round by letterboxing the scene domain to a centered square.
 * @param {boolean} is3D
 */
function syncSkyPathBoxToAltitude(is3D = true) {
    const altEl = document.getElementById('altitudePlot');
    const skyEl = document.getElementById('skyPathPlot');
    if (!altEl || !skyEl || !window.Plotly) return;
    const w = Math.max(320, Math.floor(altEl.getBoundingClientRect().width));
    const h = Math.max(320, Math.floor(altEl.getBoundingClientRect().height));
    const update = { width: w, height: h };
    if (is3D) {
        // compute square scene domain centered within [0,1]×[0,1]
        let x0 = 0, x1 = 1, y0 = 0, y1 = 1;
        if (w > h) {
            const span = h / w; // fraction of width to use
            x0 = (1 - span) / 2;
            x1 = x0 + span;
        } else if (h > w) {
            const span = w / h; // fraction of height to use
            y0 = (1 - span) / 2;
            y1 = y0 + span;
        }
        update.scene = { domain: { x: [x0, x1], y: [y0, y1] } };

        // Install wheel guard so scroll zoom only applies when cursor is inside the 3D scene domain
        try {
            installSkyPathWheelGuard(x0, x1, y0, y1);
        } catch (_) {}
    }
    Plotly.relayout('skyPathPlot', update);
}

/**
 * Make skySpherePlot use exactly the same width/height as altitudePlot's box.
 * For 3D, keep the dome round by letterboxing the scene domain to a centered square.
 * @param {boolean} is3D
 */
function syncSkySphereBoxToAltitude(is3D = true) {
    const altEl = document.getElementById('altitudePlot');
    const skyEl = document.getElementById('skySpherePlot');
    if (!altEl || !skyEl || !window.Plotly) return;
    const w = Math.max(320, Math.floor(altEl.getBoundingClientRect().width));
    const h = Math.max(320, Math.floor(altEl.getBoundingClientRect().height));
    const update = { width: w, height: h };
    if (is3D) {
        // For 3D, keep the scene square and centered
        const size = Math.min(w, h);
        const x0 = (w - size) / 2 / w;
        const x1 = (w + size) / 2 / w;
        const y0 = (h - size) / 2 / h;
        const y1 = (h + size) / 2 / h;
        update['scene.domain.x'] = [x0, x1];
        update['scene.domain.y'] = [y0, y1];
        try {
            installSkySphereWheelGuard(x0, x1, y0, y1);
        } catch (_) {}
    }
    Plotly.relayout('skySpherePlot', update);
}

// Allow zoom only when the mouse is within the current 3D scene domain box
function installSkyPathWheelGuard(x0, x1, y0, y1) {
    const skyEl = document.getElementById('skyPathPlot');
    if (!skyEl) return;

    // Remove any existing handler
    if (skyEl._wheelGuard) {
        try { skyEl.removeEventListener('wheel', skyEl._wheelGuard, { capture: true }); } catch (_) {}
    }

    const handler = (e) => {
        const rect = skyEl.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const w = rect.width;
        const h = rect.height;
        const within = (x >= x0 * w && x <= x1 * w && y >= y0 * h && y <= y1 * h);
        // If outside the scene domain, block Plotly's zoom handling but allow page scroll
        if (!within) {
            e.stopPropagation();
            e.stopImmediatePropagation();
        }
    };

    skyEl._wheelGuard = handler;
    // Capture phase to intercept before Plotly's internal listener
    skyEl.addEventListener('wheel', handler, { capture: true, passive: false });
}

// Allow zoom only when the mouse is within the current 3D scene domain box for sphere plot
function installSkySphereWheelGuard(x0, x1, y0, y1) {
    const skyEl = document.getElementById('skySpherePlot');
    if (!skyEl) return;

    // Remove any existing handler
    if (skyEl._wheelHandler) {
        skyEl.removeEventListener('wheel', skyEl._wheelHandler, { capture: true });
    }
    
    const handler = (e) => {
        const rect = skyEl.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width;
        const y = (e.clientY - rect.top) / rect.height;
        
        // Only allow zoom if mouse is within the scene domain
        if (x >= x0 && x <= x1 && y >= y0 && y <= y1) {
            // Let Plotly handle the zoom - don't prevent default
            return;
        } else {
            // Prevent zoom and page scroll outside the scene domain
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
        }
    };
    
    skyEl._wheelHandler = handler;
    // Capture phase to intercept before Plotly's internal listener
    skyEl.addEventListener('wheel', handler, { capture: true, passive: false });
}

/**
 * Setup scroll prevention for 3D plots
 */
function setup3DPlotScrollPrevention() {
    // Add a global wheel event listener to prevent page scrolling when over 3D plots
    document.addEventListener('wheel', (e) => {
        const target = e.target;
        const skySpherePlot = document.getElementById('skySpherePlot');
        
        // Check if the wheel event is over the 3D sphere plot
        if (skySpherePlot && (skySpherePlot.contains(target) || target === skySpherePlot)) {
            // Check if the mouse is over the actual 3D scene area
            const rect = skySpherePlot.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;
            
            // Only prevent scrolling if mouse is over the plot area
            if (x >= 0 && x <= rect.width && y >= 0 && y <= rect.height) {
                // Allow the wheel event to be handled by Plotly for zooming
                // but prevent it from bubbling up to cause page scroll
                e.stopPropagation();
            }
        }
    }, { passive: false, capture: true });
}

/**
 * Calculate imaging hours
 */
async function calculateImagingHours() {
    const targetType = document.body.getAttribute('data-target-type');
    const usePlanet = document.getElementById('usePlanetTarget').checked;
    const isComet = targetType === 'comet' && !usePlanet;

    if (isComet) {
        const cometDesignation = document.body.getAttribute('data-comet-designation');
        showLoading(`Calculating comet ${cometDesignation} visibility...`, true);
        updateProgress(10, 'Initializing comet ephemeris calculations...');
    } else {
        showLoading();
    }
    
    // Check if using manual coordinates
    const useManualCoords = document.getElementById('useManualCoords').checked && !usePlanet && !isComet;
    
    const params = {
        object_name: document.getElementById('objectName').value,
        latitudes: document.getElementById('latitudes').value,
        longitudes: document.getElementById('longitudes').value,
        year: document.getElementById('calculationYear').value,
        samples_per_month: 31, // default: daily sampling
        altitude_threshold: document.getElementById('altitudeThreshold').value,
        sun_alt_threshold: document.getElementById('sunAltThreshold').value,
        enable_moon_alt_filter: document.getElementById('enableMoonAltFilter').checked,
        moon_alt_threshold: document.getElementById('moonAltThreshold').value,
        enable_moon_sep_filter: document.getElementById('enableMoonSepFilter').checked,
        moon_sep_threshold: document.getElementById('moonSepThreshold').value,
        enable_moon_phase_filter: document.getElementById('enableMoonPhaseFilter').checked,
        moon_phase_threshold: document.getElementById('moonPhaseThreshold').value
    };
    
    // Planet mode
    if (usePlanet) {
        const planetName = document.getElementById('planetName').value;
        params.planet = { name: planetName };
        params.object_name = planetName;
    }

    // Comet mode
    if (isComet) {
        const cometDesignation = document.body.getAttribute('data-comet-designation');
        params.comet = { designation: cometDesignation };
        params.object_name = cometDesignation;
        // Speed-up: use coarse sampling for yearly overview to keep UX responsive
        params.samples_per_month = 2; // bi-monthly
        console.log('Comet mode: reducing samples_per_month to', params.samples_per_month, 'for responsiveness');
    }

    // Add manual coordinates if being used
    if (useManualCoords) {
        const raInput = document.getElementById('manualRA').value;
        const decInput = document.getElementById('manualDec').value;
        const customName = document.getElementById('manualObjectName').value || 'Custom Object';
        
        if (!raInput.trim() || !decInput.trim()) {
            hideLoading();
            showError('Please enter both RA and Dec coordinates');
            return;
        }
        
        const ra = parseRA(raInput);
        const dec = parseDecl(decInput);
        
        if (ra === null) {
            hideLoading();
            showError('Invalid RA format. Use decimal hours (e.g., 10.6847) or HMS format (e.g., 10h 41m 4.5s)');
            return;
        }
        
        if (dec === null) {
            hideLoading();
            showError('Invalid Dec format. Use decimal degrees (e.g., 41.2686) or DMS format (e.g., +41° 16\' 8.4")');
            return;
        }
        
        params.manual_coords = {
            ra_hours: ra,
            dec_degrees: dec,
            name: customName
        };
        params.object_name = customName; // Use custom name for display
    }

    try {
        if (isComet) {
            updateProgress(30, 'Downloading comet ephemeris from JPL Horizons...');
            await new Promise(resolve => setTimeout(resolve, 200)); // Brief delay to show progress
        }

        const result = await app.calculateImagingHours(params);

        if (isComet) {
            updateProgress(70, 'Processing comet visibility calculations...');
            await new Promise(resolve => setTimeout(resolve, 300));
        }

        hideLoading();

        if (result.success) {
            app.displayResults(result);
            updateLocationSelector();

            if (isComet) {
                showLoading('Generating comet altitude and sky path plots...', true);
                updateProgress(80, 'Creating altitude plot with comet positions...');
                await new Promise(resolve => setTimeout(resolve, 200));
            }

            // Auto-generate altitude plot
            await createAltitudePlot();

            if (isComet) {
                updateProgress(100, 'Comet plots complete!');
                await new Promise(resolve => setTimeout(resolve, 500));
                hideLoading();
            }

            // Scroll to results section so user knows plots are available
            const resultsSection = document.getElementById('resultsSection');
            if (resultsSection) {
                setTimeout(() => {
                    resultsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 100);
            }
        } else {
            showError(result.error);
        }
    } catch (error) {
        hideLoading();
        showError('Calculation error: ' + error.message);
    }
}

// Incremented per search so a slow SIMBAD lookup can't overwrite a newer result
let latestObjectSearch = 0;

/**
 * Search for object coordinates
 */
async function searchObject() {
    const searchId = ++latestObjectSearch;
    setObjectSearchBusy(false);

    // Check if using manual coordinates or planet target
    const usePlanet = document.getElementById('usePlanetTarget').checked;
    const useManualCoords = document.getElementById('useManualCoords').checked && !usePlanet;
    
    if (useManualCoords) {
        const raInput = document.getElementById('manualRA').value;
        const decInput = document.getElementById('manualDec').value;
        const customName = document.getElementById('manualObjectName').value || 'Custom Object';
        
        if (!raInput.trim() || !decInput.trim()) {
            showError('Please enter both RA and Dec coordinates');
            return;
        }
        
        const ra = parseRA(raInput);
        const dec = parseDecl(decInput);
        
        if (ra === null) {
            showError('Invalid RA format. Use decimal hours (e.g., 10.6847) or HMS format (e.g., 10h 41m 4.5s)');
            return;
        }
        
        if (dec === null) {
            showError('Invalid Dec format. Use decimal degrees (e.g., 41.2686) or DMS format (e.g., +41° 16\' 8.4")');
            return;
        }
        
        // Clear comet mode when using manual coordinates
        document.body.removeAttribute('data-target-type');
        document.body.removeAttribute('data-comet-designation');

        displayObjectCoordinates(customName, ra, dec);
        return;
    }
    // If planet selected, bypass catalog lookup
    if (usePlanet) {
        // Clear comet mode when using planet targets
        clearCometMode();

        const planetName = document.getElementById('planetName').value;
        hideObjectCoordinates();
        document.getElementById('objectName').value = planetName;
        return;
    }

    // Original object search logic
    const objectName = document.getElementById('objectName').value;
    if (!objectName) {
        showError('Please enter an object name');
        return;
    }

    // Check if this looks like a comet designation
    if (app.calculator.isCometDesignation(objectName)) {
        await searchComet(objectName);
        return;
    }

    // Clear comet mode when searching for regular objects
    document.body.removeAttribute('data-target-type');
    document.body.removeAttribute('data-comet-designation');

    // Don't leave the previous object's coordinates or error up while SIMBAD is queried
    hideObjectCoordinates();
    document.getElementById('errorAlert').style.display = 'none';
    setObjectSearchBusy(true);

    try {
        const coords = await app.getObjectCoordinates(objectName);
        if (searchId !== latestObjectSearch) return; // a newer search replaced this one
        if (coords) {
            displayObjectCoordinates(objectName, coords.ra_hours, coords.dec_degrees);
        } else {
            hideObjectCoordinates();
            showError(`Object '${objectName}' not found`);
        }
    } catch (error) {
        if (searchId !== latestObjectSearch) return;
        hideObjectCoordinates();
        showError('Search error: ' + error.message);
    } finally {
        if (searchId === latestObjectSearch) setObjectSearchBusy(false);
    }
}

/**
 * Show a spinner on the search button while a lookup runs (SIMBAD can take seconds)
 * @param {boolean} busy
 */
function setObjectSearchBusy(busy) {
    const button = document.getElementById('searchBtn');
    const icon = button?.querySelector('i');
    if (!button || !icon) return;
    icon.className = busy ? 'fas fa-spinner fa-spin' : 'fas fa-search';
    button.setAttribute('aria-busy', String(busy));
}

/**
 * Search for comet by designation
 * @param {string} designation - Comet designation (e.g., "C/2023 A3")
 */
async function searchComet(designation) {
    showLoading(`Searching for comet ${designation}...`, true);

    try {
        // Step 1: Validate comet exists in JPL database
        updateProgress(25, 'Validating comet designation with JPL Small-Body Database...');
        const validation = await app.calculator.validateCometDesignation(designation);

        if (!validation.valid) {
            hideLoading();
            showError(`Comet "${designation}" not found: ${validation.error}`);
            return;
        }

        // Step 2: Display comet info
        updateProgress(50, 'Comet found! Preparing orbital elements...');

        // Add a small delay to show progress
        await new Promise(resolve => setTimeout(resolve, 500));

        updateProgress(75, 'Loading comet information...');
        displayCometInfo(designation, validation);

        // Step 3: Cache this comet for the session
        updateProgress(100, 'Comet ready for calculations!');
        sessionStorage.setItem(`comet_${designation}`, JSON.stringify(validation));

        // Small delay to show completion
        await new Promise(resolve => setTimeout(resolve, 500));
        hideLoading();

    } catch (error) {
        hideLoading();
        console.error('Comet search failed:', error);
        showError('Error searching for comet. Please check the designation and try again.');
    }
}

/**
 * Display comet information
 * @param {string} designation - Comet designation
 * @param {Object} info - Comet validation info
 */
function displayCometInfo(designation, info) {
    const coordsDiv = document.getElementById('objectCoordinates');

    let discoveryText = '';
    if (info.discoveryInfo && info.discoveryInfo.date) {
        discoveryText = `<p class="mb-1"><strong>Discovery:</strong> ${info.discoveryInfo.date}</p>`;
    }

    coordsDiv.innerHTML = `
        <div class="alert alert-success">
            <h6><i class="fas fa-comet"></i> Comet Found: ${info.fullName}</h6>
            <p class="mb-1"><strong>Type:</strong> ${info.kind}</p>
            ${discoveryText}
            <p class="mb-0"><strong>Note:</strong> Comet positions are calculated dynamically using live JPL ephemeris data.</p>
        </div>
    `;
    coordsDiv.style.display = 'block';

    // Mark that we're using comet mode
    document.body.setAttribute('data-target-type', 'comet');
    document.body.setAttribute('data-comet-designation', designation);
}

/**
 * Fetch object suggestions
 */
async function fetchSuggestions(query) {
    try {
        const suggestions = await app.searchObjects(query, 15);
        displaySuggestions(suggestions);
    } catch (error) {
        console.error('Error fetching suggestions:', error);
    }
}

/**
 * Display suggestions dropdown
 */
function displaySuggestions(_suggestions) {
    const dropdown = document.getElementById('suggestionsDropdown');
    if (dropdown) { dropdown.style.display = 'none'; dropdown.innerHTML = ''; }
}

/**
 * Hide suggestions dropdown
 */
function hideSuggestions() {
    const dd = document.getElementById('suggestionsDropdown');
    if (dd) dd.style.display = 'none';
}

/**
 * Update active selection in suggestions
 */
function updateActiveSelection(items, activeIndex) {
    items.forEach((item, index) => {
        if (index === activeIndex) {
            item.classList.add('active');
            item.style.backgroundColor = '#007bff';
            item.style.color = 'white';
        } else {
            item.classList.remove('active');
            item.style.backgroundColor = '';
            item.style.color = '';
        }
    });
}

/**
 * Select an object from suggestions
 */
function selectObject(objectName) {
    document.getElementById('objectName').value = objectName;
    hideSuggestions();
    searchObject();
}

/**
 * Create altitude plot
 */
async function createAltitudePlot() {
    const locationSelector = document.getElementById('locationSelector');
    let selectedTimezone = locationSelector.getAttribute('data-timezone');
    
    if (!selectedTimezone) {
        const lat = parseFloat(document.getElementById('altitudeLatitude').value);
        const lon = parseFloat(document.getElementById('altitudeLongitude').value);
        selectedTimezone = app.calculator.getTimezoneForLocation(lat, lon);
    }
    
    const params = {
        object_name: document.getElementById('objectName').value,
        latitude: document.getElementById('altitudeLatitude').value,
        longitude: document.getElementById('altitudeLongitude').value,
        elevation: document.getElementById('altitudeElevation').value,
        obs_date: document.getElementById('altitudeDate').value,
        timezone: selectedTimezone,
        horizon_data: document.getElementById('horizonData') ? document.getElementById('horizonData').value : ''
    };
    
    // Planet target mode
    const usePlanet = document.getElementById('usePlanetTarget').checked;
    if (usePlanet) {
        const planetName = document.getElementById('planetName').value;
        params.planet = { name: planetName };
        params.object_name = planetName;
    }

    // Comet target mode (planet/Moon mode takes precedence over a stale comet)
    const targetType = document.body.getAttribute('data-target-type');
    if (targetType === 'comet' && !usePlanet) {
        const cometDesignation = document.body.getAttribute('data-comet-designation');
        params.comet = { designation: cometDesignation };
        params.object_name = cometDesignation;
    }

    // Add manual coordinates if being used
    const useManualCoords = document.getElementById('useManualCoords').checked && !usePlanet && targetType !== 'comet';
    if (useManualCoords) {
        const raInput = document.getElementById('manualRA').value;
        const decInput = document.getElementById('manualDec').value;
        const customName = document.getElementById('manualObjectName').value || 'Custom Object';
        
        if (raInput.trim() && decInput.trim()) {
            const ra = parseRA(raInput);
            const dec = parseDecl(decInput);
            
            if (ra !== null && dec !== null) {
                params.manual_coords = {
                    ra_hours: ra,
                    dec_degrees: dec,
                    name: customName
                };
                params.object_name = customName; // Use custom name for display
            }
        }
    }

    try {
        const result = await app.createAltitudePlot(params);
        
        if (result.success) {
            app.plotlyManager.displayPlot('altitudePlot', result.plot);

            // Also render both Sky Path views (2D Polar and 3D Sphere)
            try {
                await renderSkyPath2D();
            } catch (e) {
                console.warn('2D Sky view rendering failed:', e);
            }
            
            try {
                await renderSkySphere3D();
            } catch (e) {
                console.warn('3D Sky view rendering failed:', e);
            }
            
            // Force resize after plots creation to ensure full width
            setTimeout(() => {
                app.plotlyManager.resizeAllPlots();
            }, 200);
        } else {
            showError(result.error);
        }
    } catch (error) {
        showError('Altitude plot error: ' + error.message);
    }
}

/**
 * Update location selector with current coordinates
 */
function updateLocationSelector() {
    const latitudes = document.getElementById('latitudes').value.split(',').map(s => parseFloat(s.trim()));
    const longitudes = document.getElementById('longitudes').value.split(',').map(s => parseFloat(s.trim()));
    
    const locationSelector = document.getElementById('locationSelector');
    locationSelector.innerHTML = '<option value="custom">Custom Location</option>';
    
    for (let i = 0; i < latitudes.length && i < longitudes.length; i++) {
        const lat = latitudes[i];
        const lon = longitudes[i];
        const timezone = app.calculator.getTimezoneForLocation(lat, lon);
        
        const latStr = formatLatitude(lat, 1);
        const lonStr = formatLongitude(lon, 1);
        const locationName = `Location ${i + 1}: ${latStr}, ${lonStr}`;
        
        const option = document.createElement('option');
        option.value = `${lat},${lon},${timezone}`;
        option.textContent = locationName;
        locationSelector.appendChild(option);
    }
    
    if (latitudes.length > 0 && longitudes.length > 0) {
        const timezone = app.calculator.getTimezoneForLocation(latitudes[0], longitudes[0]);
        locationSelector.value = `${latitudes[0]},${longitudes[0]},${timezone}`;
        locationSelector.dispatchEvent(new Event('change'));
    }
}

/**
 * Display object coordinates
 */
function displayObjectCoordinates(objectName, raHours, decDegrees) {
    const raFormatted = formatRA(raHours);
    const decFormatted = formatDec(decDegrees);
    
    const coordsDiv = document.getElementById('objectCoordinates');
    coordsDiv.innerHTML = `
        <div class="alert alert-info py-2">
            <strong><i class="fas fa-crosshairs"></i> Object Coordinates:</strong>
            <span id="coordinatesText">RA: ${raFormatted} | Dec: ${decFormatted} <small class="text-muted">(${raHours.toFixed(4)}h, ${decDegrees.toFixed(4)}°)</small></span>
        </div>
    `;
    coordsDiv.style.display = 'block';
}

/**
 * Hide object coordinates
 */
function hideObjectCoordinates() {
    document.getElementById('objectCoordinates').style.display = 'none';
}

/**
 * Show loading indicator
 */
function showLoading(message = 'Calculating imaging hours... This may take a few moments.', showProgress = false) {
    document.getElementById('loading').style.display = 'block';
    document.getElementById('resultsSection').style.display = 'none';
    document.getElementById('errorAlert').style.display = 'none';
    document.getElementById('loadingMessage').textContent = message;

    if (showProgress) {
        document.getElementById('loadingProgress').style.display = 'block';
        updateProgress(0, 'Starting...');
    } else {
        document.getElementById('loadingProgress').style.display = 'none';
    }
}

/**
 * Update progress bar
 */
function updateProgress(percentage, text) {
    const progressBar = document.getElementById('progressBar');
    const progressText = document.getElementById('progressText');

    if (progressBar) {
        progressBar.style.width = `${percentage}%`;
        progressBar.setAttribute('aria-valuenow', percentage);
    }

    if (progressText && text) {
        progressText.textContent = text;
    }
}

/**
 * Parse Right Ascension from various formats
 * @param {string} raStr - RA string in decimal hours or HMS format
 * @returns {number|null} - RA in decimal hours or null if invalid
 */
function parseRA(raStr) {
    if (!raStr || typeof raStr !== 'string') return null;
    
    const trimmed = raStr.trim();
    
    // Try HMS format: 10h 41m 4.5s or 10:41:04.5 or 10 41 4.5
    const hmsMatch = trimmed.match(/^(\d{1,2})\s*[h:]\s*(\d{1,2})\s*[m:']\s*(\d{1,2}(?:\.\d+)?)\s*s?$/i);
    if (hmsMatch) {
        const hours = parseInt(hmsMatch[1]);
        const minutes = parseInt(hmsMatch[2]);
        const seconds = parseFloat(hmsMatch[3]);
        
        if (hours >= 0 && hours < 24 && minutes >= 0 && minutes < 60 && seconds >= 0 && seconds < 60) {
            return hours + minutes / 60 + seconds / 3600;
        }
    }
    
    // Try simple HMS without separators: 104104.5
    const hmsSimpleMatch = trimmed.match(/^(\d{2})(\d{2})(\d{2}(?:\.\d+)?)$/);
    if (hmsSimpleMatch) {
        const hours = parseInt(hmsSimpleMatch[1]);
        const minutes = parseInt(hmsSimpleMatch[2]);
        const seconds = parseFloat(hmsSimpleMatch[3]);
        
        if (hours >= 0 && hours < 24 && minutes >= 0 && minutes < 60 && seconds >= 0 && seconds < 60) {
            return hours + minutes / 60 + seconds / 3600;
        }
    }
    
    // Try decimal format
    const decimal = parseFloat(trimmed);
    if (!isNaN(decimal) && decimal >= 0 && decimal < 24) {
        return decimal;
    }
    
    return null;
}

/**
 * Parse Declination from various formats
 * @param {string} decStr - Dec string in decimal degrees or DMS format
 * @returns {number|null} - Dec in decimal degrees or null if invalid
 */
function parseDecl(decStr) {
    if (!decStr || typeof decStr !== 'string') return null;
    
    const trimmed = decStr.trim();
    
    // Try DMS format: +41° 16' 8.4" or -41:16:08.4 or +41 16 8.4
    const dmsMatch = trimmed.match(/^([+-]?)(\d{1,2})\s*[°:]\s*(\d{1,2})\s*['']\s*(\d{1,2}(?:\.\d+)?)\s*"?$/);
    if (dmsMatch) {
        const sign = dmsMatch[1] === '-' ? -1 : 1;
        const degrees = parseInt(dmsMatch[2]);
        const minutes = parseInt(dmsMatch[3]);
        const seconds = parseFloat(dmsMatch[4]);
        
        if (degrees >= 0 && degrees <= 90 && minutes >= 0 && minutes < 60 && seconds >= 0 && seconds < 60) {
            const decimal = degrees + minutes / 60 + seconds / 3600;
            return sign * decimal;
        }
    }
    
    // Try simple DMS without separators: +411608.4
    const dmsSimpleMatch = trimmed.match(/^([+-]?)(\d{2})(\d{2})(\d{2}(?:\.\d+)?)$/);
    if (dmsSimpleMatch) {
        const sign = dmsSimpleMatch[1] === '-' ? -1 : 1;
        const degrees = parseInt(dmsSimpleMatch[2]);
        const minutes = parseInt(dmsSimpleMatch[3]);
        const seconds = parseFloat(dmsSimpleMatch[4]);
        
        if (degrees >= 0 && degrees <= 90 && minutes >= 0 && minutes < 60 && seconds >= 0 && seconds < 60) {
            const decimal = degrees + minutes / 60 + seconds / 3600;
            return sign * decimal;
        }
    }
    
    // Try decimal format
    const decimal = parseFloat(trimmed);
    if (!isNaN(decimal) && decimal >= -90 && decimal <= 90) {
        return decimal;
    }
    
    return null;
}

/**
 * Hide loading indicator
 */
function hideLoading() {
    document.getElementById('loading').style.display = 'none';
}

/**
 * Show error message
 */
function showError(message) {
    document.getElementById('errorMessage').textContent = message;
    document.getElementById('errorAlert').style.display = 'block';
    hideLoading();
}

/**
 * Force dot decimal format in number inputs
 */
function forceDotDecimals() {
    const numberInputs = document.querySelectorAll('input[type="number"]');
    numberInputs.forEach(input => {
        if (input.value) {
            input.value = input.value.replace(',', '.');
        }
    });
}

/**
 * Update status indicator
 */
function updateStatusIndicator() {
    const indicator = document.getElementById('statusIndicator');
    const isOnline = navigator.onLine;
    
    if (isOnline) {
        indicator.innerHTML = '<div class="online-indicator"><i class="fas fa-wifi"></i> Online</div>';
    } else {
        indicator.innerHTML = '<div class="offline-indicator"><i class="fas fa-wifi-slash"></i> Offline</div>';
    }
}

// Global variables for animation
let skyPathPlotData = null;
let skySpherePlotData = null;
let skyPathAnimationInterval = null;
let skySphereAnimationInterval = null;

/**
 * Setup manual coordinates toggle functionality
 */
function setupManualCoordinatesToggle() {
    const checkbox = document.getElementById('useManualCoords');
    const manualSection = document.getElementById('manualCoordsSection');
    const objectNameInput = document.getElementById('objectName');
    const searchBtn = document.getElementById('searchBtn');
    const planetCheckbox = document.getElementById('usePlanetTarget');
    const planetSection = document.getElementById('planetTargetSection');
    
    if (!checkbox || !manualSection || !objectNameInput || !searchBtn) return;
    
    checkbox.addEventListener('change', (e) => {
        const isChecked = e.target.checked;
        manualSection.style.display = isChecked ? 'block' : 'none';
        
        // Disable/enable object name search when using manual coords
        objectNameInput.disabled = isChecked;
        searchBtn.disabled = false; // Keep search button enabled for coordinate validation
        
        if (isChecked) {
            // Turn off planet mode if enabling manual
            if (planetCheckbox) planetCheckbox.checked = false;
            if (planetSection) planetSection.style.display = 'none';
            updateMoonFilterAvailability();
            // Clear any existing coordinates display when switching to manual
            hideObjectCoordinates();
        } else {
            // Clear manual inputs when switching back to search
            document.getElementById('manualRA').value = '';
            document.getElementById('manualDec').value = '';
            document.getElementById('manualObjectName').value = '';
            
            // Re-search if object name exists
            if (objectNameInput.value.trim()) {
                searchObject();
            }
        }
    });
}

/**
 * Setup Planet target toggle functionality
 */
/**
 * Leave comet mode (set by a comet search) so a planet/Moon target isn't mislabelled
 */
function clearCometMode() {
    document.body.removeAttribute('data-target-type');
    document.body.removeAttribute('data-comet-designation');
}

// Moon filter controls that don't apply when the Moon itself is the target
const MOON_FILTER_CONTROL_IDS = [
    'enableMoonAltFilter', 'moonAltThreshold',
    'enableMoonSepFilter', 'moonSepThreshold',
    'enableMoonPhaseFilter', 'moonPhaseThreshold'
];

/**
 * Whether planet mode is on with the Moon selected
 */
function isMoonTargetSelected() {
    const planetCheckbox = document.getElementById('usePlanetTarget');
    const planetSelect = document.getElementById('planetName');
    return !!(planetCheckbox && planetCheckbox.checked && planetSelect && planetSelect.value === 'Moon');
}

/**
 * Disable the Moon filter controls (keeping their state) while the Moon is the target
 */
function updateMoonFilterAvailability() {
    const moonTarget = isMoonTargetSelected();
    MOON_FILTER_CONTROL_IDS.forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.disabled = moonTarget;
    });
    const note = document.getElementById('moonTargetFilterNote');
    if (note) note.style.display = moonTarget ? '' : 'none';
}

function setupPlanetToggle() {
    const planetCheckbox = document.getElementById('usePlanetTarget');
    const planetSection = document.getElementById('planetTargetSection');
    const manualCheckbox = document.getElementById('useManualCoords');
    const manualSection = document.getElementById('manualCoordsSection');
    const objectNameInput = document.getElementById('objectName');
    const searchBtn = document.getElementById('searchBtn');
    const planetSelect = document.getElementById('planetName');

    if (!planetCheckbox || !planetSection || !objectNameInput || !searchBtn) return;

    planetCheckbox.addEventListener('change', (e) => {
        const isChecked = e.target.checked;
        planetSection.style.display = isChecked ? 'block' : 'none';

        // If enabling planet mode, disable manual coords and object search
        if (isChecked) {
            if (manualCheckbox) {
                manualCheckbox.checked = false;
            }
            if (manualSection) {
                manualSection.style.display = 'none';
            }
            objectNameInput.disabled = true;
            clearCometMode();
            // Sync object name to selected planet and run a calculation for immediate feedback
            if (planetSelect) {
                objectNameInput.value = planetSelect.value;
            }
            hideObjectCoordinates();
            updateMoonFilterAvailability();
            try { calculateImagingHours(); } catch (_) {}
        } else {
            objectNameInput.disabled = false;
            updateMoonFilterAvailability();
        }
    });

    // React to planet changes by updating object field and recomputing
    if (planetSelect) {
        planetSelect.addEventListener('change', () => {
            if (planetCheckbox.checked) {
                clearCometMode();
                objectNameInput.value = planetSelect.value;
                hideObjectCoordinates();
                updateMoonFilterAvailability();
                try { calculateImagingHours(); } catch (_) {}
            }
        });
    }
}

/**
 * Setup Moon filter toggle functionality for both altitude and separation filters
 */
function setupMoonFilterToggles() {
    // Moon Altitude Filter
    const moonAltCheckbox = document.getElementById('enableMoonAltFilter');
    const moonAltSection = document.getElementById('moonAltFilterSection');

    if (moonAltCheckbox && moonAltSection) {
        moonAltCheckbox.addEventListener('change', (e) => {
            const isChecked = e.target.checked;
            moonAltSection.style.display = isChecked ? 'block' : 'none';
        });
    }

    // Moon Separation Filter
    const moonSepCheckbox = document.getElementById('enableMoonSepFilter');
    const moonSepSection = document.getElementById('moonSepFilterSection');

    if (moonSepCheckbox && moonSepSection) {
        moonSepCheckbox.addEventListener('change', (e) => {
            const isChecked = e.target.checked;
            moonSepSection.style.display = isChecked ? 'block' : 'none';
        });
    }

    // Faint-Moon (phase-aware) filter
    const moonPhaseCheckbox = document.getElementById('enableMoonPhaseFilter');
    const moonPhaseSection = document.getElementById('moonPhaseFilterSection');

    if (moonPhaseCheckbox && moonPhaseSection) {
        moonPhaseCheckbox.addEventListener('change', (e) => {
            const isChecked = e.target.checked;
            moonPhaseSection.style.display = isChecked ? 'block' : 'none';
        });
    }
}

/**
 * Setup Sky Path time controls
 */
function setupSkyPathTimeControls() {
    const slider = document.getElementById('skyPathTimeSlider');
    const playBtn = document.getElementById('skyPathPlayBtn');
    const resetBtn = document.getElementById('skyPathResetBtn');
    const currentTimeSpan = document.getElementById('skyPathCurrentTime');

    if (!slider || !playBtn || !resetBtn || !currentTimeSpan) return;

    // Slider change handler
    slider.addEventListener('input', (e) => {
        if (!skyPathPlotData) return;
        
        const sliderValue = parseInt(e.target.value);
        const len = (skyPathPlotData.timePoints?.length || skyPathPlotData.timeLabels?.length || 0);
        const timeIndex = Math.floor((sliderValue / 100) * (len - 1));
        updateSkyPathMarkers(timeIndex);
    });

    // Play/pause button
    playBtn.addEventListener('click', () => {
        if (skyPathAnimationInterval) {
            // Stop animation
            clearInterval(skyPathAnimationInterval);
            skyPathAnimationInterval = null;
            playBtn.innerHTML = '<i class="fas fa-play"></i>';
        } else {
            // Start animation
            startSkyPathAnimation();
            playBtn.innerHTML = '<i class="fas fa-pause"></i>';
        }
    });

    // Reset button
    resetBtn.addEventListener('click', () => {
        slider.value = 0;
        if (skyPathPlotData) {
            updateSkyPathMarkers(0);
        }
        if (skyPathAnimationInterval) {
            clearInterval(skyPathAnimationInterval);
            skyPathAnimationInterval = null;
            playBtn.innerHTML = '<i class="fas fa-play"></i>';
        }
    });
}

/**
 * Setup 3D Sphere time controls
 */
function setupSkySphereTimeControls() {
    const slider = document.getElementById('skySphereTimeSlider');
    const playBtn = document.getElementById('skySpherePlayBtn');
    const resetBtn = document.getElementById('skySphereResetBtn');
    const currentTimeSpan = document.getElementById('skySphereCurrentTime');

    if (!slider || !playBtn || !resetBtn || !currentTimeSpan) return;

    // Slider change handler
    slider.addEventListener('input', (e) => {
        if (!skySpherePlotData) return;
        
        const sliderValue = parseInt(e.target.value);
        const len = (skySpherePlotData.timePoints?.length || skySpherePlotData.timeLabels?.length || 0);
        const timeIndex = Math.floor((sliderValue / 100) * (len - 1));
        updateSkySphereMarkers(timeIndex);
    });

    // Play/pause button
    playBtn.addEventListener('click', () => {
        if (skySphereAnimationInterval) {
            // Stop animation
            clearInterval(skySphereAnimationInterval);
            skySphereAnimationInterval = null;
            playBtn.innerHTML = '<i class="fas fa-play"></i>';
        } else {
            // Start animation
            startSkySphereAnimation();
            playBtn.innerHTML = '<i class="fas fa-pause"></i>';
        }
    });

    // Reset button
    resetBtn.addEventListener('click', () => {
        slider.value = 0;
        if (skySpherePlotData) {
            updateSkySphereMarkers(0);
        }
        if (skySphereAnimationInterval) {
            clearInterval(skySphereAnimationInterval);
            skySphereAnimationInterval = null;
            playBtn.innerHTML = '<i class="fas fa-play"></i>';
        }
    });
}

/**
 * Update sky path markers for a specific time index
 */
function updateSkyPathMarkers(timeIndex) {
    if (!skyPathPlotData || !skyPathPlotData.timePoints) return;

    const slider = document.getElementById('skyPathTimeSlider');
    const currentTimeSpan = document.getElementById('skyPathCurrentTime');
    
    // Clamp time index
    timeIndex = Math.max(0, Math.min(timeIndex, skyPathPlotData.timePoints.length - 1));
    
    // Update slider position
    if (slider) {
        slider.value = Math.round((timeIndex / (skyPathPlotData.timePoints.length - 1)) * 100);
    }
    
    // Update time display
    if (currentTimeSpan) {
        if (skyPathPlotData.timeLabels && skyPathPlotData.timeLabels[timeIndex]) {
            currentTimeSpan.textContent = skyPathPlotData.timeLabels[timeIndex];
        } else if (skyPathPlotData.timePoints && skyPathPlotData.timePoints[timeIndex]) {
            const time = skyPathPlotData.timePoints[timeIndex];
            currentTimeSpan.textContent = time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } else {
            currentTimeSpan.textContent = '--:--';
        }
    }

    // Update Moon separation display
    const moonSepSpan = document.getElementById('skyPathMoonSep');
    if (moonSepSpan && skyPathPlotData.moonSeparations) {
        const separation = skyPathPlotData.moonSeparations[timeIndex];
        if (separation !== null && separation !== undefined) {
            moonSepSpan.textContent = separation.toFixed(1) + '°';
        } else {
            moonSepSpan.textContent = '--°';
        }
    }

    // Check if this is 2D or 3D plot and update accordingly
    if (skyPathPlotData.objPolar) {
        // 2D Polar plot updates
        // Update object marker position (trace index 2)
        if (skyPathPlotData.objPolar.theta[timeIndex] !== null && skyPathPlotData.objPolar.r[timeIndex] !== null) {
            const objUpdate = {
                theta: [[skyPathPlotData.objPolar.theta[timeIndex]]],
                r: [[skyPathPlotData.objPolar.r[timeIndex]]],
                text: [[app.formatTimeFromArray(skyPathPlotData.timePoints, timeIndex)]]
            };
            Plotly.restyle('skyPathPlot', objUpdate, [2]);
        }

        // Update moon marker position (trace index 3)
        if (skyPathPlotData.moonPolar.theta[timeIndex] !== null && skyPathPlotData.moonPolar.r[timeIndex] !== null) {
            const moonUpdate = {
                theta: [[skyPathPlotData.moonPolar.theta[timeIndex]]],
                r: [[skyPathPlotData.moonPolar.r[timeIndex]]],
                text: [[app.formatTimeFromArray(skyPathPlotData.timePoints, timeIndex)]]
            };
            Plotly.restyle('skyPathPlot', moonUpdate, [3]);
        }
    } else if (skyPathPlotData.objCoords) {
        // 3D Sphere plot updates
        // Update object marker position (trace index 3 - after surface, obj path, moon path)
        if (skyPathPlotData.objCoords.x[timeIndex] !== null && 
            skyPathPlotData.objCoords.y[timeIndex] !== null && 
            skyPathPlotData.objCoords.z[timeIndex] !== null) {
            const objUpdate = {
                x: [[skyPathPlotData.objCoords.x[timeIndex]]],
                y: [[skyPathPlotData.objCoords.y[timeIndex]]],
                z: [[skyPathPlotData.objCoords.z[timeIndex]]],
                text: [[app.formatTimeFromArray(skyPathPlotData.timePoints, timeIndex)]]
            };
            Plotly.restyle('skyPathPlot', objUpdate, [3]);
        }

        // Update moon marker position (trace index 4)
        if (skyPathPlotData.moonCoords.x[timeIndex] !== null && 
            skyPathPlotData.moonCoords.y[timeIndex] !== null && 
            skyPathPlotData.moonCoords.z[timeIndex] !== null) {
            const moonUpdate = {
                x: [[skyPathPlotData.moonCoords.x[timeIndex]]],
                y: [[skyPathPlotData.moonCoords.y[timeIndex]]],
                z: [[skyPathPlotData.moonCoords.z[timeIndex]]],
                text: [[app.formatTimeFromArray(skyPathPlotData.timePoints, timeIndex)]]
            };
            Plotly.restyle('skyPathPlot', moonUpdate, [4]);
        }
    }
}

/**
 * Start sky path animation
 */
function startSkyPathAnimation() {
    if (!skyPathPlotData) return;

    const slider = document.getElementById('skyPathTimeSlider');
    if (!slider) return;

    const startValue = parseInt(slider.value);
    let currentValue = startValue;

    skyPathAnimationInterval = setInterval(() => {
        currentValue += 1;
        if (currentValue > 100) {
            // Loop back to start
            currentValue = 0;
        }

        const timeIndex = Math.floor((currentValue / 100) * (skyPathPlotData.timePoints.length - 1));
        updateSkyPathMarkers(timeIndex);
    }, 200); // Update every 200ms
}

/**
 * Update sky sphere markers for a specific time index
 */
function updateSkySphereMarkers(timeIndex) {
    if (!skySpherePlotData || !skySpherePlotData.timePoints) return;

    const slider = document.getElementById('skySphereTimeSlider');
    const currentTimeSpan = document.getElementById('skySphereCurrentTime');
    
    // Clamp time index
    timeIndex = Math.max(0, Math.min(timeIndex, skySpherePlotData.timePoints.length - 1));
    
    // Update slider position
    if (slider) {
        slider.value = Math.round((timeIndex / (skySpherePlotData.timePoints.length - 1)) * 100);
    }
    
    // Update time display
    if (currentTimeSpan) {
        if (skySpherePlotData.timeLabels && skySpherePlotData.timeLabels[timeIndex]) {
            currentTimeSpan.textContent = skySpherePlotData.timeLabels[timeIndex];
        } else if (skySpherePlotData.timePoints && skySpherePlotData.timePoints[timeIndex]) {
            const time = skySpherePlotData.timePoints[timeIndex];
            currentTimeSpan.textContent = time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } else {
            currentTimeSpan.textContent = '--:--';
        }
    }

    // Update Moon separation display
    const moonSepSpan = document.getElementById('skySphereMoonSep');
    if (moonSepSpan && skySpherePlotData.moonSeparations) {
        const separation = skySpherePlotData.moonSeparations[timeIndex];
        if (separation !== null && separation !== undefined) {
            moonSepSpan.textContent = separation.toFixed(1) + '°';
        } else {
            moonSepSpan.textContent = '--°';
        }
    }

    // Check if this is 2D or 3D plot and update accordingly
    if (skySpherePlotData.objCoords) {
        // 3D Sphere plot updates
        // Update object marker position (trace index 3 - after surface, obj path, moon path)
        if (skySpherePlotData.objCoords.x[timeIndex] !== null && 
            skySpherePlotData.objCoords.y[timeIndex] !== null && 
            skySpherePlotData.objCoords.z[timeIndex] !== null) {
            const objUpdate = {
                x: [[skySpherePlotData.objCoords.x[timeIndex]]],
                y: [[skySpherePlotData.objCoords.y[timeIndex]]],
                z: [[skySpherePlotData.objCoords.z[timeIndex]]],
                text: [[app.formatTimeFromArray(skySpherePlotData.timePoints, timeIndex)]]
            };
            Plotly.restyle('skySpherePlot', objUpdate, [3]);
        }

        // Update moon marker position (trace index 4)
        if (skySpherePlotData.moonCoords.x[timeIndex] !== null && 
            skySpherePlotData.moonCoords.y[timeIndex] !== null && 
            skySpherePlotData.moonCoords.z[timeIndex] !== null) {
            const moonUpdate = {
                x: [[skySpherePlotData.moonCoords.x[timeIndex]]],
                y: [[skySpherePlotData.moonCoords.y[timeIndex]]],
                z: [[skySpherePlotData.moonCoords.z[timeIndex]]],
                text: [[app.formatTimeFromArray(skySpherePlotData.timePoints, timeIndex)]]
            };
            Plotly.restyle('skySpherePlot', moonUpdate, [4]);
        }
    }
}

/**
 * Start sky sphere animation
 */
function startSkySphereAnimation() {
    if (!skySpherePlotData) return;

    const slider = document.getElementById('skySphereTimeSlider');
    if (!slider) return;

    const startValue = parseInt(slider.value);
    const maxValue = 100;
    let currentValue = startValue;

    skySphereAnimationInterval = setInterval(() => {
        currentValue = (currentValue + 1) % (maxValue + 1);
        slider.value = currentValue;
        
        const len = (skySpherePlotData.timePoints?.length || skySpherePlotData.timeLabels?.length || 0);
        const timeIndex = Math.floor((currentValue / 100) * (len - 1));
        updateSkySphereMarkers(timeIndex);
    }, 200); // Update every 200ms
}

// Initialize the application when the DOM is loaded
document.addEventListener('DOMContentLoaded', initializeApp);
